import { useState, useEffect } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { X, EnvelopeSimple, PaperPlaneTilt, FilePdf } from "@phosphor-icons/react";

/**
 * Compact email dialog that ships the current repair ticket to the
 * customer as a PDF attachment (rendered server-side by
 * `_render_ticket_slip_pdf`). Backed by `POST /api/repair-tickets/{id}/email`.
 *
 * Default recipient is prefilled from the ticket's customer email.
 * Staff can add an optional message that appears in the email body.
 * Empty message = polite default template (subject "Repair <ticket#>
 * booked").
 */
export default function SendTicketEmailModal({ open, onClose, ticket, onSent }) {
  const [to, setTo] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTo(ticket?.customer_email || "");
    setMessage("");
  }, [open, ticket]);

  const send = async () => {
    const clean = (to || "").trim();
    if (!clean) {
      toast.error("Enter an email address");
      return;
    }
    // Basic client-side guard — the backend validates properly with EmailStr.
    if (!/^\S+@\S+\.\S+$/.test(clean)) {
      toast.error("That doesn't look like a valid email address");
      return;
    }
    setSending(true);
    try {
      await api.post(`/repair-tickets/${ticket.id}/email`, {
        to: clean,
        message: message.trim() || undefined,
      });
      toast.success(`Emailed ticket PDF to ${clean}`);
      onSent?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't send email");
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-950/55 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="send-ticket-email-scrim"
    >
      <div
        className="bg-white w-full max-w-md border border-[#E5E7EB] rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="send-ticket-email-modal"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E7EB]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-1">
              EMAIL TICKET PDF
            </div>
            <h2 className="font-heading font-black text-xl tracking-tight">
              {ticket?.ticket_number || "Ticket"}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[#F3F4F6]"
            data-testid="send-ticket-email-close"
            aria-label="Close"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="rounded-lg bg-[#EFF6FF] border border-[#BFDBFE] px-3 py-2 flex items-start gap-2">
            <FilePdf size={18} weight="duotone" className="text-[#1E5FEB] mt-0.5 shrink-0" />
            <div className="text-[12px] leading-snug text-[#1E40AF]">
              The ticket slip PDF (device details, services, estimated cost,
              warranty text) will be attached to the email.
            </div>
          </div>

          <label className="block">
            <div className="text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-semibold mb-1">
              Send to
            </div>
            <input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="customer@example.com"
              className="w-full h-11 px-3 border border-[#E5E7EB] focus:border-[#1E5FEB] focus:outline-none text-sm bg-white"
              data-testid="send-ticket-email-input"
              autoFocus
            />
            {!ticket?.customer_email && (
              <div className="text-[11px] text-amber-600 mt-1">
                No email on file for this customer — type one above.
              </div>
            )}
          </label>

          <label className="block">
            <div className="text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-semibold mb-1">
              Optional message
            </div>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Hi Jane, please keep this receipt for pickup. Thanks!"
              rows={3}
              className="w-full px-3 py-2 border border-[#E5E7EB] focus:border-[#1E5FEB] focus:outline-none text-sm bg-white resize-none"
              data-testid="send-ticket-email-message"
            />
            <div className="text-[11px] text-zinc-500 mt-1">
              Appears below the intake summary in the email body.
            </div>
          </label>
        </div>

        <div className="flex gap-2 p-4 border-t border-[#E5E7EB] bg-[#FAFBFC]">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 h-11 rounded-xl border border-[#E5E7EB] bg-white hover:bg-slate-50 text-[13px] font-semibold text-slate-700 transition-colors"
            data-testid="send-ticket-email-cancel"
          >
            Cancel
          </button>
          <button
            onClick={send}
            disabled={sending}
            className="flex-1 h-11 rounded-xl bg-[#1E5FEB] hover:bg-[#1849C8] disabled:bg-zinc-300 text-white text-sm font-semibold flex items-center justify-center gap-2"
            data-testid="send-ticket-email-submit"
          >
            <PaperPlaneTilt size={14} weight="bold" />
            {sending ? "Sending…" : "Send email"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Re-export the icon so callers can trigger the modal with a matching
// button without pulling the icon in themselves.
export { EnvelopeSimple };
